/**
 * Quick log (core quicklog.ts): one tap for what a team does many times a day. The owner: "all they have to do is press
 * one button depending on their role to log the events and when they happened."
 *  - QuickLogCard (For You): the person's top 6 buttons; one tap logs; "Logged 2:14 PM ✓ Undo" for 30 seconds;
 *    "Which trash?" only when there's more than one; a pair's step 2 is the next button once step 1 is logged. Gentle
 *    nudge cards ("Has anyone checked the kitchen trash?") with [I checked it] and [Nobody needs to].
 *  - KioskQuickLog (the shop tablet): the same buttons for the whole team; tap, pick your name, type your PIN, logged
 *    for you (POST /kiosk/quicklog; the server checks the PIN).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { quickOpensForm } from "@logwave/core";
import type { KioskConfig } from "../lib/kiosk.ts";
import { clearPinFails, lockLeft, pinFailed } from "../lib/kiosk.ts";
import { loggedLine, needsPicker, pickerTitle, stripButtons } from "../lib/quicklog.ts";
import { savedOf } from "../lib/checkoff.ts";
import { useCheckoff } from "./Checkoff.tsx";
import { saveErrorText } from "../lib/failures.ts";
import { useLoad } from "../lib/useLoad.ts";
import { ApiError, newAttemptKey, type NudgeView, type QuickButtonView, type QuickLogResponse } from "../api.ts";
import { useApi, useSession } from "../session.tsx";
import { Icon } from "./Icons.tsx";
import { PinPad } from "./PinPad.tsx";
import { Button, ErrorBox, Sheet } from "./ui.tsx";

/** A tap's words for the check-off toast: "Took out the kitchen trash. Logged 2:14 PM ✓ (Sam)". */
const toastLine = (r: QuickLogResponse, tz?: string) => `${r.line}. ${loggedLine(r.at, tz)}${r.employee ? ` (${r.employee.name.split(" ")[0]})` : ""}`;

/** Buttons + "Which one?" picker. `onLog` does the saving (the phone's own account, or the tablet's PIN step). */
function Buttons({ buttons, onLog, busy }: { buttons: QuickButtonView[]; onLog(b: QuickButtonView, objectId?: string): void; busy?: string }) {
  const [pick, setPick] = useState<QuickButtonView>();
  const navigate = useNavigate();
  // A button whose rule needs a reading (the sanitizer bucket's strip) opens that check's form; it never logs on one tap (core checkinput.ts).
  const press = (b: QuickButtonView) => { const form = quickOpensForm(b.actionId); if (form) navigate(`/do?log=${encodeURIComponent(form)}`); else if (needsPicker(b)) setPick(b); else onLog(b, b.objects[0]?.id); };
  return (
    <>
      <div className="quick-grid" role="group" aria-label="Quick log">
        {buttons.map((b) => (
          <Button key={b.key} variant={b.next ? "primary" : "secondary"} busy={busy === b.key} onClick={() => press(b)}>
            {b.next ? `Next: ${b.label}` : b.label}
          </Button>
        ))}
      </div>
      {pick && (
        <Sheet title={pickerTitle(pick)} onClose={() => setPick(undefined)}>
          <div className="btn-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
            {pick.objects.map((o) => (
              <Button key={o.id} block variant="secondary" onClick={() => { setPick(undefined); onLog(pick, o.id); }}>{o.line}</Button>
            ))}
          </div>
        </Sheet>
      )}
    </>
  );
}

/** For You: the strip and the gentle questions. `ready`: the person may do food work today (hidden otherwise). */
/** A tap's key while it hasn't saved yet (a retry after an error reuses it). */
const quickAttempts = new Map<string, string>();

export function QuickLogCard({ ready = true }: { ready?: boolean }) {
  const api = useApi();
  const s = useSession();
  const tz = s.location.timezone;
  const cat = useLoad(() => api.quickCatalog(s.org.id, s.location.id), [api, s.org.id, s.location.id]);
  const nudges = useLoad(() => api.quickNudges(s.org.id, s.location.id).then((r) => r.nudges).catch(() => [] as NudgeView[]), [api, s.org.id, s.location.id]);
  const [next, setNext] = useState<QuickButtonView>();
  const [busy, setBusy] = useState<string>();
  const [err, setErr] = useState<string>();
  const checkoff = useCheckoff();
  if (!ready || !cat.data || cat.data.buttons.length === 0) return null;

  async function log(actionId: string, key: string, objectId?: string) {
    setBusy(key); setErr(undefined);
    try {
      // The same tap again after an error is the same attempt (audit #6): the server logs it once.
      const sig = `${actionId}|${objectId ?? ""}`;
      const idempotencyKey = quickAttempts.get(sig) ?? newAttemptKey();
      quickAttempts.set(sig, idempotencyKey);
      const r = await api.quickLog(s.org.id, { locationId: s.location.id, actionId, ...(objectId ? { objectId } : {}), idempotencyKey });
      quickAttempts.delete(sig);
      // The one check-off toast (DESIGN §3b): "Took out the kitchen trash. Logged 2:14 PM ✓" with Undo for 30 s.
      checkoff.done(r.line, savedOf("quick", s.org.id, { id: r.log.id, at: r.at }), { text: toastLine(r, tz), onUndone: () => { setNext(undefined); void cat.reload(); void nudges.reload(); } });
      setNext(r.next);
      void cat.reload();
      void nudges.reload();
    } catch (e) { setErr(saveErrorText(e)); } finally { setBusy(undefined); }
  }
  async function dismiss(n: NudgeView) {
    try { await api.dismissNudge(s.org.id, { locationId: s.location.id, actionId: n.actionId, ...(n.objectId ? { objectId: n.objectId } : {}) }); void nudges.reload(); } catch (e) { setErr(saveErrorText(e)); }
  }

  return (
    <>
      {(nudges.data ?? []).map((n) => (
        <li className="feed-item" key={n.key}>
          <article className="fcard fcard-info">
            <header className="fcard-head"><span className="fcard-av" aria-hidden="true"><Icon name="clock" size={20} /></span><span className="fcard-kind">A question</span></header>
            <div className="fcard-body">
              <h2 className="fcard-title">{n.title}</h2>
              <p>{n.body}</p>
              <div className="btn-row">
                <Button busy={busy === n.key} onClick={() => void log(n.actionId, n.key, n.objectId)}>I checked it</Button>
                <Button variant="secondary" onClick={() => void dismiss(n)}>Nobody needs to</Button>
              </div>
            </div>
          </article>
        </li>
      ))}
      <li className="feed-item">
        <article className="fcard">
          <header className="fcard-head"><span className="fcard-av" aria-hidden="true"><Icon name="check" size={20} /></span><span className="fcard-kind">Quick log</span></header>
          <div className="fcard-body">
            <h2 className="fcard-title">Did one of these? Tap it.</h2>
            <Buttons buttons={stripButtons(cat.data.buttons, next)} busy={busy} onLog={(b, objectId) => void log(b.actionId, b.key, objectId)} />
            {err && <ErrorBox error={err} />}
          </div>
        </article>
      </li>
    </>
  );
}

type Person = { id: string; name: string };

/** The shop tablet: tap what you did, pick your name, type your PIN. Nothing about anyone else is shown. */
export function KioskQuickLog({ config, people }: { config: KioskConfig; people: Person[] }) {
  const api = useApi();
  const s = useSession();
  const tz = (s.locations.find((l) => l.id === config.locationId) ?? s.location).timezone;
  const cat = useLoad(() => api.quickCatalog(config.orgId, config.locationId), [api, config.orgId, config.locationId]);
  const [pending, setPending] = useState<{ b: QuickButtonView; objectId?: string; who?: Person }>();
  const [next, setNext] = useState<QuickButtonView>();
  const [busy, setBusy] = useState(false);
  const checkoff = useCheckoff();
  const [err, setErr] = useState<string>();
  if (!cat.data || cat.data.buttons.length === 0 || people.length === 0) return null;

  async function tryPin(pin: string) {
    if (!pending?.who) return;
    setBusy(true); setErr(undefined);
    try {
      const r = await api.kioskQuickLog(config.orgId, { employeeId: pending.who.id, pin, locationId: config.locationId, actionId: pending.b.actionId, ...(pending.objectId ? { objectId: pending.objectId } : {}) });
      clearPinFails();
      checkoff.done(r.line, savedOf("quick", config.orgId, { id: r.log.id, at: r.at }), { text: toastLine(r, tz), onUndone: () => { setNext(undefined); void cat.reload(); } });
      setNext(r.next); setPending(undefined);
      void cat.reload();
    } catch (e) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 429)) { pinFailed(); setErr(e.status === 429 ? e.message : "That PIN didn't match. Try again."); }
      else setErr(saveErrorText(e));
    } finally { setBusy(false); }
  }

  return (
    <section aria-label="Quick log" style={{ marginTop: "var(--s5)" }}>
      <h2>Did one of these? Tap it.</h2>
      <Buttons buttons={stripButtons(cat.data.buttons, next)} onLog={(b, objectId) => { setErr(undefined); setPending({ b, objectId }); }} />
      {pending && (
        <Sheet title={pending.who ? `Hi ${pending.who.name.split(" ")[0]}. Type your PIN.` : "Who did it?"} onClose={() => setPending(undefined)}>
          {!pending.who ? (
            <div className="btn-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
              {people.map((p) => <Button key={p.id} block variant="secondary" onClick={() => setPending({ ...pending, who: p })}>{p.name}</Button>)}
            </div>
          ) : (
            <>
              <ErrorBox error={err} />
              <PinPad label="Your 4 numbers" onPin={(p) => void tryPin(p)} busy={busy} disabled={lockLeft() > 0} />
            </>
          )}
        </Sheet>
      )}
    </section>
  );
}
